/**
 * PropertyEditor Type Definitions
 *
 * Defines the core interfaces and types for the property editor system.
 * Following SOLID principles: Single Responsibility, no tight coupling
 */

import type { FormControl, FieldValidation } from '@atlas-forms/types-js';

/**
 * Core property editor interface
 * Represents a single editor that can configure a specific control type
 *
 * SRP: Each editor is responsible for configuring ONE control type
 */
export interface PropertyEditor<T extends FormControl = FormControl> {
  /**
   * Control type this editor handles (e.g., 'card-container', 'bar-chart')
   * Must match FormControl.type exactly
   */
  type: string;

  /**
   * Display label for the editor shown to user
   * Example: 'Card Container', 'Bar Chart'
   */
  label: string;

  /**
   * Optional description of what this editor does
   */
  description?: string;

  /**
   * Render function to display property editor UI
   * Called when control is selected in designer
   */
  render: (props: PropertyEditorProps<T>) => React.ReactNode;

  /**
   * Optional: Version of this editor
   * Useful for tracking when editors change
   */
  version?: string;

  /**
   * Optional: Icon for this editor (SVG or component)
   */
  icon?: React.ReactNode;
}

/**
 * Props passed to PropertyEditor.render()
 * SRP: Handlers are separated by concern (config, validation, basic, control)
 */
export interface PropertyEditorProps<T extends FormControl = FormControl> {
  /**
   * Currently selected control being edited
   */
  control: T;

  /**
   * Update handler for control.config properties
   * Use when changing control-type-specific configuration
   *
   * Example: onConfigChange('datasource', { fields, rows })
   */
  onConfigChange: (key: string, value: unknown) => void;

  /**
   * Update handler for FormControl root properties
   * Use when changing universal properties (label, required, etc.)
   *
   * Example: onBasicChange('label', 'New Label')
   */
  onBasicChange: (key: keyof Omit<FormControl, 'config'>, value: unknown) => void;

  /**
   * Update handler for control.validation properties
   * Use when changing validation rules
   *
   * Example: onValidationChange('minLength', 5)
   */
  onValidationChange: (key: keyof FieldValidation, value: unknown) => void;

  /**
   * Update handler for complex control updates
   * Use when making multiple changes at once
   *
   * Example: onControlChange({ config: {...}, validation: {...} })
   */
  onControlChange: (updates: Partial<T>) => void;
}

/**
 * Type guard to check if a value is a PropertyEditor
 */
export function isPropertyEditor(value: unknown): value is PropertyEditor {
  if (!value || typeof value !== 'object') return false;
  const editor = value as Record<string, unknown>;
  return (
    typeof editor.type === 'string' &&
    typeof editor.label === 'string' &&
    typeof editor.render === 'function'
  );
}

/**
 * Plugin interface for extending the editor registry
 * SRP: Plugins handle registration/unregistration of their editors
 */
export interface PropertyEditorPlugin {
  /**
   * Unique identifier for this plugin
   */
  id: string;

  /**
   * Human-readable name
   */
  name: string;

  /**
   * Called when plugin is loaded
   * Should register all editors provided by this plugin
   */
  initialize(registry: PropertyEditorRegistry): void;

  /**
   * Called when plugin is unloaded
   * Should unregister all editors from this plugin
   */
  unregister(registry: PropertyEditorRegistry): void;

  /**
   * Optional: Version of this plugin
   */
  version?: string;
}

/**
 * Registry interface
 * Provides methods for managing property editors
 * SRP: Registry is only responsible for editor discovery and lookup
 */
export interface PropertyEditorRegistry {
  /**
   * Register a property editor
   * Overwrites existing editor of same type
   */
  register(editor: PropertyEditor): void;

  /**
   * Get editor for a specific control type
   * Returns undefined if editor not found
   */
  get<T extends FormControl = FormControl>(type: string): PropertyEditor<T> | undefined;

  /**
   * Get all registered editors
   */
  getAll(): PropertyEditor[];

  /**
   * Check if editor exists for a control type
   */
  has(type: string): boolean;

  /**
   * Unregister an editor
   */
  unregister(type: string): boolean;

  /**
   * Register a plugin
   */
  registerPlugin(plugin: PropertyEditorPlugin): void;

  /**
   * Get count of registered editors
   */
  size(): number;
}

/**
 * Base configuration interface
 * All control configs should extend this
 * SRP: Config interfaces only define data shape, not behavior
 */
export interface ControlConfig {
  [key: string]: unknown;
}

/**
 * Discriminated union helper
 * Helps with type-safe control handling in editors
 */
export type TypedFormControl<T extends string = string> = FormControl & {
  type: T;
  config: Record<string, unknown>;
};

/**
 * Error thrown when editor operation fails
 */
export class PropertyEditorError extends Error {
  constructor(
    public readonly type: string,
    message: string,
  ) {
    super(`PropertyEditor(${type}): ${message}`);
    this.name = 'PropertyEditorError';
  }
}
